/**
 * TransferTargetStep - Step 3: Seleção do destino da transferência
 * 
 * v2.5.0: Adiciona suporte a departamentos como destino (conversations_department)
 * v2.3.0: Permite selecionar para quem os registros serão transferidos:
 * - Modo DIRETO: Selecionar agente/vendedor/departamento específico
 * - Modo FILA: Selecionar fila de distribuição automática
 * 
 * Exclui automaticamente o agente/vendedor/departamento de origem da lista.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { 
  TransferType, 
  TRANSFER_TYPE_LABELS, 
  TransferTargetMode, 
  UNASSIGNED_SOURCE 
} from '@/hooks/useBulkTransfer';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Check, 
  User, 
  ArrowRight, 
  Users, 
  Shuffle,
  UserX,
  Building2
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// TIPOS
// =============================================================================

interface TransferTargetStepProps {
  transferType: TransferType;
  selectedId: string | null;
  excludeId: string | null;
  targetMode: TransferTargetMode;
  queueId: string | null;
  onSelectId: (id: string) => void;
  onTargetModeChange: (mode: TransferTargetMode) => void;
  onQueueIdChange: (id: string | null) => void;
}

interface Agent {
  id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
}

interface Seller {
  id: string;
  name: string;
  email: string | null;
  avatar_url: string | null;
  user_id: string | null;
}

interface DistributionQueue {
  id: string;
  name: string;
  distribution_method: string;
  is_active: boolean;
}

// v2.5.0: Interface para departamento
interface Department {
  id: string;
  name: string;
  description: string | null;
}

// =============================================================================
// COMPONENTE
// =============================================================================

export function TransferTargetStep({
  transferType,
  selectedId,
  excludeId,
  targetMode,
  queueId,
  onSelectId,
  onTargetModeChange,
  onQueueIdChange,
}: TransferTargetStepProps) {
  const { currentTenant } = useAuth();
  const [search, setSearch] = useState('');

  // Determinar tipo de transferência
  const isAgentTransfer = transferType === 'conversations' || transferType === 'deals_assigned';
  const isSellerTransfer = transferType === 'deals_seller' || transferType === 'contacts_seller';
  const isDepartmentTransfer = transferType === 'conversations_department'; // v2.5.0
  
  // v2.5.0: Modo queue NÃO disponível para departamento
  const supportsQueueMode = (isAgentTransfer || transferType === 'deals_seller') && !isDepartmentTransfer;
  
  // v2.3.0: Verificar se origem é "sem atribuição"
  const isUnassignedSource = excludeId === UNASSIGNED_SOURCE;

  // =========================================================================
  // QUERY: AGENTES
  // =========================================================================

  // v2.0.0: Membros do tenant via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembers = [], isLoading: isLoadingAgents } = useTenantMembers({
    onlyActive: true,
    enabled: isAgentTransfer && targetMode === 'direct' && !!currentTenant?.id,
  });
  const agents: Agent[] | undefined = tenantMembers.map(m => ({
    id: m.user_id,
    full_name: m.full_name || '',
    email: m.email || '',
    avatar_url: m.avatar_url,
  }));

  // =========================================================================
  // QUERY: VENDEDORES
  // =========================================================================

  const { data: sellers, isLoading: isLoadingSellers } = useQuery({
    queryKey: ['transfer-sellers-target', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('crm_sellers')
        .select(`id, name, email, user_id, profiles:user_id (avatar_url)`)
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return (data || []).map((s) => ({
        id: s.id,
        name: s.name,
        email: s.email,
        user_id: s.user_id,
        avatar_url: (s.profiles as { avatar_url: string | null } | null)?.avatar_url || null,
      })) as Seller[];
    },
    enabled: isSellerTransfer && !!currentTenant?.id,
  });

  // =========================================================================
  // v2.5.0: QUERY: DEPARTAMENTOS (destino)
  // =========================================================================

  const { data: departments, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['transfer-departments-target', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase
        .from('departments')
        .select('id, name, description')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return (data || []) as Department[];
    },
    enabled: isDepartmentTransfer && !!currentTenant?.id,
  });

  // =========================================================================
  // v2.3.0: QUERY: FILAS DE DISTRIBUIÇÃO
  // =========================================================================

  const { data: queues, isLoading: isLoadingQueues } = useQuery({
    queryKey: ['distribution-queues-for-transfer', currentTenant?.id, transferType],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const contextType = transferType === 'conversations' ? 'chat' : 'crm';
      const { data, error } = await supabase
        .from('distribution_queues')
        .select('id, name, distribution_method, is_active')
        .eq('tenant_id', currentTenant.id)
        .in('context_type', [contextType, 'all'])
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return (data || []) as DistributionQueue[];
    },
    enabled: supportsQueueMode && targetMode === 'queue' && !!currentTenant?.id,
  });

  // =========================================================================
  // FILTRAR ITENS (excluindo origem)
  // =========================================================================

  const filteredItems = useMemo(() => {
    const searchLower = search.toLowerCase();

    if (isAgentTransfer && agents) {
      return agents
        .filter((a) => isUnassignedSource || a.id !== excludeId)
        .filter((a) =>
          a.full_name?.toLowerCase().includes(searchLower) ||
          a.email?.toLowerCase().includes(searchLower)
        );
    }

    if (isSellerTransfer && sellers) {
      return sellers
        .filter((s) => isUnassignedSource || s.id !== excludeId)
        .filter((s) =>
          s.name?.toLowerCase().includes(searchLower) ||
          s.email?.toLowerCase().includes(searchLower)
        );
    }

    // v2.5.0: Filtrar departamentos (excluindo origem)
    if (isDepartmentTransfer && departments) {
      return departments
        .filter((d) => isUnassignedSource || d.id !== excludeId)
        .filter((d) => d.name?.toLowerCase().includes(searchLower));
    }

    return [];
  }, [isAgentTransfer, isSellerTransfer, isDepartmentTransfer, agents, sellers, departments, search, excludeId, isUnassignedSource]);

  const isLoading = targetMode === 'queue' 
    ? isLoadingQueues 
    : isDepartmentTransfer 
      ? isLoadingDepartments
      : (isAgentTransfer ? isLoadingAgents : isLoadingSellers);

  // Label dinâmico
  const entityLabel = isDepartmentTransfer ? 'departamento' : isAgentTransfer ? 'agente' : 'vendedor';

  /** Retorna o label do método de distribuição */
  const getDistributionMethodLabel = (method: string) => {
    switch (method) {
      case 'round_robin': return 'Rodízio';
      case 'least_busy': return 'Menos Ocupado';
      case 'weighted': return 'Ponderado';
      default: return method;
    }
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">
          {isDepartmentTransfer ? 'Para qual departamento transferir?' : 'Para quem transferir?'}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Selecione {isDepartmentTransfer ? 'o departamento' : isAgentTransfer ? 'o agente' : 'o vendedor'} de destino para{' '}
          {TRANSFER_TYPE_LABELS[transferType].toLowerCase()}
        </p>
      </div>

      {/* Indicador visual de transferência */}
      <div className="flex items-center justify-center gap-2 p-3 bg-muted/50 rounded-lg">
        {isUnassignedSource ? (
          <>
            <div className="flex items-center gap-1.5">
              {isDepartmentTransfer ? (
                <Building2 className="h-4 w-4 text-amber-600" />
              ) : (
                <UserX className="h-4 w-4 text-amber-600" />
              )}
              <span className="text-sm text-amber-600 font-medium">
                {isDepartmentTransfer ? 'Sem departamento' : 'Sem atribuição'}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 text-primary" />
          </>
        ) : (
          <>
            <span className="text-sm text-muted-foreground">Origem selecionada</span>
            <ArrowRight className="h-4 w-4 text-primary" />
          </>
        )}
        <span className="text-sm font-medium">Selecione o destino</span>
      </div>

      {/* Toggle para modo de distribuição (NÃO disponível para departamento) */}
      {supportsQueueMode && (
        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-lg",
              targetMode === 'queue' ? "bg-primary/10" : "bg-muted"
            )}>
              {targetMode === 'queue' ? (
                <Shuffle className="h-4 w-4 text-primary" />
              ) : (
                <User className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div>
              <Label htmlFor="queue-mode" className="text-sm font-medium">
                {targetMode === 'queue' ? 'Distribuir via fila' : 'Transferência direta'}
              </Label>
              <p className="text-xs text-muted-foreground">
                {targetMode === 'queue' 
                  ? 'Usa Round Robin ou Menos Ocupado'
                  : `Selecione um ${entityLabel} específico`}
              </p>
            </div>
          </div>
          <Switch
            id="queue-mode"
            checked={targetMode === 'queue'}
            onCheckedChange={(checked) => onTargetModeChange(checked ? 'queue' : 'direct')}
          />
        </div>
      )}

      {/* =========================================================== */}
      {/* MODO QUEUE: Lista de filas de distribuição */}
      {/* =========================================================== */}
      {targetMode === 'queue' && supportsQueueMode && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Selecione a fila de distribuição:
          </p>
          
          <div className="max-h-[240px] overflow-y-auto space-y-2">
            {isLoadingQueues ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="flex items-center gap-3 p-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : !queues || queues.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nenhuma fila de distribuição ativa encontrada</p>
                <p className="text-xs mt-1">
                  Crie uma fila em Configurações → Filas de Distribuição
                </p>
              </div>
            ) : (
              queues.map((queue) => {
                const isSelected = queueId === queue.id;
                
                return (
                  <Card
                    key={queue.id}
                    className={cn(
                      'cursor-pointer transition-all hover:border-primary/50',
                      isSelected && 'border-primary bg-primary/5 ring-1 ring-primary'
                    )}
                    onClick={() => onQueueIdChange(queue.id)}
                  >
                    <CardContent className="flex items-center gap-3 p-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Shuffle className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{queue.name}</p>
                        <Badge variant="secondary" className="text-xs">
                          {getDistributionMethodLabel(queue.distribution_method)}
                        </Badge>
                      </div>
                      {isSelected && (
                        <div className="p-1 rounded-full bg-primary text-primary-foreground">
                          <Check className="h-4 w-4" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* =========================================================== */}
      {/* MODO DIRECT: Lista de agentes/vendedores/departamentos */}
      {/* =========================================================== */}
      {(targetMode === 'direct' || isDepartmentTransfer) && (
        <>
          {/* Campo de busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Buscar ${entityLabel}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Lista de itens */}
          <div className="max-h-[240px] overflow-y-auto space-y-2">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="flex items-center gap-3 p-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {isDepartmentTransfer ? (
                  <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                ) : (
                  <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                )}
                <p>
                  {search
                    ? `Nenhum ${entityLabel} encontrado`
                    : `Nenhum ${entityLabel} disponível`}
                </p>
              </div>
            ) : (
              filteredItems.map((item) => {
                const id = item.id;
                const name = 'full_name' in item ? item.full_name : item.name;
                const email = 'email' in item ? item.email : null;
                const avatarUrl = 'avatar_url' in item ? item.avatar_url : null;
                const isSelected = selectedId === id;

                return (
                  <Card
                    key={id}
                    className={cn(
                      'cursor-pointer transition-all hover:border-primary/50',
                      isSelected && 'border-primary bg-primary/5 ring-1 ring-primary'
                    )}
                    onClick={() => onSelectId(id)}
                  >
                    <CardContent className="flex items-center gap-3 p-3">
                      {isDepartmentTransfer ? (
                        <div className={cn(
                          "h-10 w-10 rounded-full flex items-center justify-center",
                          isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                        )}>
                          <Building2 className="h-5 w-5" />
                        </div>
                      ) : (
                        <Avatar>
                          <AvatarImage src={avatarUrl || undefined} />
                          <AvatarFallback>
                            {name?.substring(0, 2).toUpperCase() || '??'}
                          </AvatarFallback>
                        </Avatar>
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{name || 'Sem nome'}</p>
                        {email && (
                          <p className="text-sm text-muted-foreground truncate">
                            {email}
                          </p>
                        )}
                        {/* v2.5.0: Descrição do departamento */}
                        {'description' in item && (item as Department).description && (
                          <p className="text-sm text-muted-foreground truncate">
                            {(item as Department).description}
                          </p>
                        )}
                      </div>

                      {isSelected && (
                        <div className="p-1 rounded-full bg-primary text-primary-foreground">
                          <Check className="h-4 w-4" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
